import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  DataRow,
  DataTable,
  Skeleton,
  useToast,
} from "@breakingthelines/design-system";
import {
  createPromoCode,
  deactivatePromoCode,
  listPromoCodes,
  type PromoCode,
  type PromoCodeDurationInput,
} from "@/lib/admin-api";
// MIN_REASON_LENGTH is admin-service's own minimum, not an advertiser rule -
// see its doc comment. Imported rather than restated so the two destructive
// actions in this tool cannot drift apart from the server.
import { MIN_REASON_LENGTH } from "@/lib/advertisers";
import {
  buildCreatePromoCodeRequest,
  canSubmitPromoCode,
  discountLabel,
  durationLabel,
  emptyPromoCodeForm,
  formatPromoCodeDate,
  promoCodeStatusLabel,
  redemptionsLabel,
  UNREADABLE_LABEL,
  type DiscountKindInput,
  type PromoCodeFormState,
} from "@/lib/promo-codes";

import styles from "./promo-codes.module.css";

/**
 * The promo codes surface: one page that creates a code, lists what exists,
 * and takes one off sale.
 *
 * # WHY IT IS A TAB OF REVENUE
 *
 * A promo code is a discount on a subscription, so it moves the same MRR the
 * neighbouring Overview tab reports. It is one page rather than a section
 * because there is nothing else under it.
 *
 * # THE TWO RULES THE TABLE EXISTS TO HOLD
 *
 *   1. An unreadable coupon renders a placeholder, never a figure. The
 *      discount arrives as a discriminated union and UNKNOWN carries no
 *      fields; rendering its absent percentage as "0% off" would describe a
 *      live discount as a harmless one. See discountLabel.
 *   2. Deactivating is not deleting. A deactivated code keeps its row, its
 *      redemption count and its history, and reads Inactive. Dropping it from
 *      the list would lose the only record that the discount ever ran.
 */

const DISCOUNT_KINDS: Array<{ id: DiscountKindInput; label: string }> = [
  { id: "PERCENT", label: "Percentage" },
  { id: "AMOUNT", label: "Fixed amount" },
];

const DURATIONS: Array<{ id: PromoCodeDurationInput; label: string }> = [
  { id: "ONCE", label: "Once" },
  { id: "REPEATING", label: "Repeating" },
  { id: "FOREVER", label: "Forever" },
];

// The platform subscription catalogue is priced in these three and there is no
// exchange rate anywhere in this system, so a code discounts one of them.
const CURRENCIES = ["gbp", "usd", "eur"];

const PAGE_SIZE = 50;

export function PromoCodesPage() {
  const toast = useToast();

  const [form, setForm] = useState<PromoCodeFormState>(emptyPromoCodeForm);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [nextPageToken, setNextPageToken] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [deactivateTarget, setDeactivateTarget] = useState<PromoCode | null>(null);
  const [deactivateReason, setDeactivateReason] = useState("");
  const [reasonTouched, setReasonTouched] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  function updateForm(patch: Partial<PromoCodeFormState>) {
    setForm((previous) => ({ ...previous, ...patch }));
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setListError(null);

    listPromoCodes({ pageSize: PAGE_SIZE })
      .then((response) => {
        if (cancelled) return;
        setCodes(response.promoCodes ?? []);
        setNextPageToken(response.nextPageToken ?? "");
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setCodes([]);
        setNextPageToken("");
        setListError(err.message || "Promo codes are unavailable.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  async function handleShowMore() {
    if (!nextPageToken || loadingMore) return;
    setLoadingMore(true);
    try {
      const response = await listPromoCodes({ pageSize: PAGE_SIZE, pageToken: nextPageToken });
      setCodes((previous) => [...previous, ...(response.promoCodes ?? [])]);
      setNextPageToken(response.nextPageToken ?? "");
    } catch (err) {
      setListError(err instanceof Error ? err.message : "Promo codes are unavailable.");
    } finally {
      setLoadingMore(false);
    }
  }

  async function handleCreate() {
    const request = buildCreatePromoCodeRequest(form);
    if (!request || creating) return;

    setCreating(true);
    setCreateError(null);
    try {
      const { promoCode } = await createPromoCode(request);
      // The new code goes straight onto the list rather than triggering a
      // refetch, which on a later page would drop it out of view again.
      setCodes((previous) => [promoCode, ...previous]);
      setForm(emptyPromoCodeForm());
      toast.success(`${promoCode.code} can be redeemed now.`, "Created");
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Could not create the code.");
    } finally {
      setCreating(false);
    }
  }

  async function handleDeactivate() {
    const reason = deactivateReason.trim();
    if (!deactivateTarget || reason.length < MIN_REASON_LENGTH || deactivating) return;

    setDeactivating(true);
    try {
      const { promoCode } = await deactivatePromoCode(deactivateTarget.id, reason);
      // Replaced in place, not removed: the row stays and reads Inactive.
      setCodes((previous) => previous.map((row) => (row.id === promoCode.id ? promoCode : row)));
      setDeactivateTarget(null);
      setDeactivateReason("");
      setReasonTouched(false);
      toast.success(`${promoCode.code} can no longer be redeemed.`, "Deactivated");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not deactivate the code.",
        "Deactivate failed",
      );
    } finally {
      setDeactivating(false);
    }
  }

  const canSubmit = canSubmitPromoCode(form) && !creating;
  const reasonValid = deactivateReason.trim().length >= MIN_REASON_LENGTH;
  const hasUnreadable = codes.some(
    (code) =>
      discountLabel(code.discount) === UNREADABLE_LABEL || durationLabel(code) === UNREADABLE_LABEL,
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Promo codes</h1>
        <p className={styles.derivation}>
          A code discounts a subscription payment. Deactivating one stops new redemptions and keeps
          the row.
        </p>
      </div>

      <section className={styles.card} aria-labelledby="promo-codes-new">
        <h2 className={styles.cardTitle} id="promo-codes-new">
          New code
        </h2>

        <div className={styles.formGrid}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Code</span>
            <input
              className={styles.input}
              type="text"
              value={form.code}
              placeholder="LAUNCH25"
              autoCapitalize="characters"
              onChange={(event) => updateForm({ code: event.currentTarget.value.toUpperCase() })}
            />
          </label>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Max redemptions</span>
            <input
              className={styles.input}
              type="number"
              min={1}
              inputMode="numeric"
              value={form.maxRedemptions}
              aria-label="Max redemptions"
              onChange={(event) => updateForm({ maxRedemptions: event.currentTarget.value })}
            />
            <p className={styles.fieldHint}>Empty is no limit.</p>
          </div>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Expires</span>
            <input
              className={styles.input}
              type="date"
              value={form.expiresOn}
              aria-label="Expires"
              min={new Date().toISOString().slice(0, 10)}
              onChange={(event) => updateForm({ expiresOn: event.currentTarget.value })}
            />
            <p className={styles.fieldHint}>Empty is no expiry.</p>
          </div>
        </div>

        <div className={styles.field}>
          <span className={styles.fieldLabel}>Discount</span>
          <div className={styles.chipRow}>
            {DISCOUNT_KINDS.map((kind) => (
              <button
                key={kind.id}
                type="button"
                aria-pressed={form.discountKind === kind.id}
                className={form.discountKind === kind.id ? styles.chipActive : styles.chip}
                onClick={() => updateForm({ discountKind: kind.id })}
              >
                {kind.label}
              </button>
            ))}
          </div>
          {form.discountKind === "PERCENT" ? (
            <div className={styles.inlineRow}>
              <input
                className={styles.numberInput}
                type="number"
                min={1}
                max={100}
                step="0.01"
                inputMode="decimal"
                value={form.percentOff}
                placeholder="25"
                aria-label="Percent off"
                onChange={(event) => updateForm({ percentOff: event.currentTarget.value })}
              />
              <span className={styles.inlineUnit}>% off</span>
            </div>
          ) : (
            <div className={styles.inlineRow}>
              <input
                className={styles.numberInput}
                type="number"
                min={0}
                step="0.01"
                inputMode="decimal"
                value={form.amountOff}
                placeholder="5.00"
                aria-label="Amount off"
                onChange={(event) => updateForm({ amountOff: event.currentTarget.value })}
              />
              <select
                className={styles.select}
                value={form.currency}
                aria-label="Currency"
                onChange={(event) => updateForm({ currency: event.currentTarget.value })}
              >
                {CURRENCIES.map((currency) => (
                  <option key={currency} value={currency}>
                    {currency.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className={styles.field}>
          <span className={styles.fieldLabel}>Applies to</span>
          <div className={styles.chipRow}>
            {DURATIONS.map((duration) => (
              <button
                key={duration.id}
                type="button"
                aria-pressed={form.duration === duration.id}
                className={form.duration === duration.id ? styles.chipActive : styles.chip}
                onClick={() => updateForm({ duration: duration.id })}
              >
                {duration.label}
              </button>
            ))}
          </div>
          {/* The months field exists only under Repeating. The request sends 0
              for the other two whatever is left in this input - see
              buildCreatePromoCodeRequest. */}
          {form.duration === "REPEATING" ? (
            <div className={styles.inlineRow}>
              <input
                className={styles.numberInput}
                type="number"
                min={1}
                step="1"
                inputMode="numeric"
                value={form.durationInMonths}
                placeholder="3"
                aria-label="Months"
                onChange={(event) => updateForm({ durationInMonths: event.currentTarget.value })}
              />
              <span className={styles.inlineUnit}>months</span>
            </div>
          ) : null}
        </div>

        {createError ? <p className={styles.errorText}>{createError}</p> : null}

        <button
          type="button"
          className={styles.primaryButton}
          disabled={!canSubmit}
          onClick={() => void handleCreate()}
        >
          {creating ? "Creating…" : "Create code"}
        </button>
      </section>

      <section className={styles.section} aria-labelledby="promo-codes-list">
        <h2 className={styles.sectionTitle} id="promo-codes-list">
          All codes
        </h2>

        {loading ? (
          // The table below is swapped out entirely while it loads, so this
          // holds its shape. `role="status"` keeps the announcement, under the
          // same label.
          <div
            className={styles.loading}
            role="status"
            aria-live="polite"
            aria-label="Loading codes"
          >
            {Array.from({ length: 5 }, (_, index) => (
              <Skeleton key={index} className="h-9" />
            ))}
          </div>
        ) : null}

        {!loading && listError ? <p className={styles.unavailable}>{listError}</p> : null}

        {!loading && !listError && codes.length === 0 ? (
          <p className={styles.empty}>No codes yet.</p>
        ) : null}

        {!loading && !listError && codes.length > 0 ? (
          <>
            <DataTable
              columns="minmax(140px, 1fr) minmax(120px, 0.9fr) minmax(110px, 0.8fr) minmax(120px, 0.8fr) minmax(120px, 0.8fr) minmax(90px, 0.6fr) minmax(120px, 0.7fr)"
              header={["Code", "Discount", "Applies to", "Redemptions", "Expires", "Status", ""]}
              headerClassName={styles.tableHeader}
            >
              {codes.map((code) => (
                <DataRow key={code.id}>
                  <span className={styles.strong} data-label="Code">
                    {code.code}
                  </span>
                  <span className={styles.meta} data-label="Discount">
                    {discountLabel(code.discount)}
                  </span>
                  <span className={styles.meta} data-label="Applies to">
                    {durationLabel(code)}
                  </span>
                  <span className={styles.meta} data-label="Redemptions">
                    {redemptionsLabel(code)}
                  </span>
                  <span className={styles.meta} data-label="Expires">
                    {formatPromoCodeDate(code.expiresAt) || "Never"}
                  </span>
                  <span data-label="Status">
                    <Badge variant={code.active ? "tintedSuccess" : "tintedNeutral"}>
                      {promoCodeStatusLabel(code)}
                    </Badge>
                  </span>
                  <span className={styles.rowActions}>
                    {code.active ? (
                      <button
                        type="button"
                        className={styles.secondaryButton}
                        onClick={() => {
                          setDeactivateTarget(code);
                          setDeactivateReason("");
                          setReasonTouched(false);
                        }}
                      >
                        Deactivate
                      </button>
                    ) : null}
                  </span>
                </DataRow>
              ))}
            </DataTable>

            {hasUnreadable ? (
              <p className={styles.note}>
                {UNREADABLE_LABEL} means the coupon behind that code could not be read, not that the
                code has no discount.
              </p>
            ) : null}

            {nextPageToken ? (
              <button
                type="button"
                className={styles.secondaryButton}
                disabled={loadingMore}
                onClick={() => void handleShowMore()}
              >
                {loadingMore ? "Loading…" : "Show more"}
              </button>
            ) : null}
          </>
        ) : null}
      </section>

      {/*
       * Kept as an AlertDialog for the same reason the membership revoke is
       * (see scholarships.tsx): Base UI hardcodes disablePointerDismissal on
       * confirmations, so an outside click cannot take a live discount off
       * sale. The reason field sits inside it because the confirm and the
       * reason gate the same call - the action stays disabled until the
       * reason is long enough, so there is no path from the row button to the
       * API without both.
       */}
      <AlertDialog
        open={deactivateTarget != null}
        onOpenChange={(open) => {
          if (open) return;
          setDeactivateTarget(null);
          setDeactivateReason("");
          setReasonTouched(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate this code</AlertDialogTitle>
            <AlertDialogDescription>
              {deactivateTarget?.code} stops being redeemable. Subscriptions already discounted by
              it keep their discount, and the code stays on this list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Reason</span>
            <textarea
              className={styles.textarea}
              rows={2}
              value={deactivateReason}
              onChange={(event) => setDeactivateReason(event.currentTarget.value)}
              onBlur={() => setReasonTouched(true)}
            />
            {reasonTouched && !reasonValid ? (
              <span className={styles.errorText}>Reason must be at least 3 characters.</span>
            ) : null}
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deactivating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={!reasonValid || deactivating}
              onClick={() => void handleDeactivate()}
            >
              {deactivating ? "Deactivating…" : "Deactivate code"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
